import type { RouteComponentProps } from '@gatsbyjs/reach-router';

import React from 'react';
import { Helmet as MetaTags } from 'react-helmet';

import {
  App_Hypotheekbond_ToolFragment,
  useGetAppHypotheekbondQuery,
} from '~/graphql/types';
import BndlLoading from '~/components/atom/BndlLoading';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import ErrorScreen from '~/components/page/ErrorScreen';
import cleanedFilename from '~/util/cleanedFilename';
import useErrorReporter from '~/hooks/useErrorReporter';
import ContentContainerDefault from '~/components/molecule/ContentContainer';
import MasterDetail from '~/components/template/MasterDetail';
import TEST_ID_MASTER_DETAIL from '~/components/template/MasterDetail/index.testid';
import Settings from './components/Settings';
import useApp from '~/hooks/useApp';
import NotFound from '../../404';
import createPageTitle from '~/util/createPageTitle';

const text = {
  appTitle: 'Nationale Hypotheekbond',
};

type Props = RouteComponentProps & {};

export type ToolMap = Record<string, App_Hypotheekbond_ToolFragment>;

const Hypotheekbond: React.FCC<Props> = ({ uri = '' }) => {
  const errorReporter = useErrorReporter();
  const { id: accountId } = useCurrentAccount();
  const { data, error, loading, updateQuery } = useGetAppHypotheekbondQuery({
    variables: {
      accountId,
    },
  });

  const {
    app,
    loading: appStatusLoading,
    error: appStatusError,
  } = useApp('AppStatus_Hypotheekbond');

  if (error || appStatusError) {
    errorReporter.captureException(
      new Error(
        `${cleanedFilename(__filename)} | An error occured (${JSON.stringify(
          error || appStatusError,
        )})`,
      ),
    );

    return <ErrorScreen />;
  }

  if (appStatusLoading || loading) return <BndlLoading />;
  if (!app?.appStatus.enabled || !data || !data.getAppHypotheekbond)
    return <NotFound />;

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.appTitle)}</title>
      </MetaTags>
      <ContentContainerDefault
        breadcrumbs={[
          {
            to: '/-/apps',
            label: 'Apps',
          },
          { label: text.appTitle },
        ]}
      >
        <MasterDetail
          basePath={uri}
          dataTestId={TEST_ID_MASTER_DETAIL.CONTAINER}
          navbar={[
            {
              type: 'link',
              to: '',
              icon: 'gear',
              name: 'Algemeen',
            },
          ]}
        >
          <Settings
            path="/"
            settings={data.getAppHypotheekbond.settings}
            updateQuery={updateData => {
              updateQuery(data => {
                if (!updateData || !data.getAppHypotheekbond) return data;

                return {
                  ...data,
                  getAppHypotheekbond: {
                    ...data.getAppHypotheekbond,
                    settings: {
                      ...data.getAppHypotheekbond?.settings,
                      ...updateData?.updateAppHypotheekbond.settings,
                    },
                  },
                };
              });
            }}
          />
        </MasterDetail>
      </ContentContainerDefault>
    </>
  );
};

export default Hypotheekbond;
